/**
 * Módulo del tutorial (grupo "Para producir mejor"): Tus reportes (KAN-232).
 *
 * El retorno de todo lo que se carga: las cuatro categorías de reportes
 * (distribución, preñez, sanidad, productividad) con sus indicadores a la
 * vista, y la exportación para compartir. Las cards NO avanzan solas al
 * clickearlas: dejan mirar adentro y se vuelve al tablero con "Ya lo hice"
 * (el runner re-navega a /reports al paso siguiente).
 *
 * Anclas: reports-nav-{distribution,pregnancy,health,productivity} (cards
 * de categoría), report-export.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';

const REPORTES = '/reports';

export const reportesModule: TutorialModuleDef = {
  id: 'reportes',
  title: 'Tus reportes, tus decisiones',
  description: 'Preñez, sanidad, productividad y distribución: todo lo que cargás, vuelto números para decidir con fundamento.',
  emoji: '📊',
  image: 'images/tutorial/reportes.png',
  permission: { module: 'reports', action: 'view' },
  grupo: 'produccion',
  route: REPORTES,
  steps: [
    {
      id: 'concepto-reportes',
      route: '/tutorial',
      text: 'Todo lo que cargaste con tanto cariño, acá te lo devolvemos en números: preñez, sanidad, pesos, distribución. Datos para decidir, no para archivar.',
      narration:
        '¿Y para qué cargar tanto animal, tanto peso, tanta vacuna? Para esto. En Reportes, la app te devuelve todo lo que le diste, vuelta número: cómo viene la preñez, cómo está la salud, cómo engordan. Datos para decidir, no para archivar en un cajón. Venite conmigo que te hago el recorrido.',
    },
    {
      id: 'llegar-a-reportes',
      route: REPORTES,
      text: 'Este es el tablero de reportes. Podés llegar desde el buscador (⌘K) buscando "reporte", o directo con el link. Cada card ya te muestra sus números clave sin entrar.',
      narration:
        'Este es el tablero. Fijate lo bueno: cada card ya te muestra los números clave ahí mismo, sin entrar a ningún lado. Nacimientos del año, diagnósticos, lo importante a primera vista. Y para llegar siempre podés usar el buscador de arriba, apretando los botones control y k, y escribiendo reporte.',
    },
    {
      id: 'distribucion',
      route: REPORTES,
      target: '[data-ar-guide="reports-nav-distribution"]',
      label: 'Distribución del Ganado',
      advanceOnClick: false,
      text: '"Distribución del Ganado": inventario, censos y demografía — cuántos tenés de cada categoría y dónde. Entrá y mirá; volvés con el ✓.',
      narration:
        'Primera card: Distribución del Ganado. Acá está el inventario vivo: cuántas vacas, cuántos terneros, cuántos toros, cómo se reparte el rodeo por categoría y por campo. El censo que antes llevabas de memoria, ahora al toque. Entrá, mirá tranquilo, y volvés con el botón de ya lo hice.',
    },
    {
      id: 'analisis-prenez',
      route: REPORTES,
      target: '[data-ar-guide="reports-nav-pregnancy"]',
      label: 'Análisis de Preñez',
      advanceOnClick: false,
      text: '"Análisis de Preñez": gestación, reproducción y ciclos — el corazón económico del rodeo, mes a mes.',
      narration:
        'Segunda card, y es la que paga las cuentas: Análisis de Preñez. Cuántas preñadas, cuántas vacías, cómo viene la reproducción del rodeo ciclo por ciclo. Un service flojo acá se ve antes de que sea un problema grave. La pregunta del año, respondida con números.',
    },
    {
      id: 'sanidad',
      route: REPORTES,
      target: '[data-ar-guide="reports-nav-health"]',
      label: 'Salud y Estado',
      advanceOnClick: false,
      text: '"Salud y Estado del Ganado": enfermedades, tratamientos y bienestar — qué tratás, a quién y cuándo.',
      narration:
        'Tercera card: Salud y Estado del Ganado. Enfermedades, tratamientos, qué lotes están con qué. Para acordarte qué aplicaste y cuándo toca el refuerzo, sin libreta empapada. El historial sanitario del campo, ordenadito.',
    },
    {
      id: 'productividad',
      route: REPORTES,
      target: '[data-ar-guide="reports-nav-productivity"]',
      label: 'Productividad y Rendimiento',
      advanceOnClick: false,
      text: '"Productividad y Rendimiento": pesos, producción y eficiencia — cuánto engorda cada lote y qué te deja. La evolución de peso también vive acá.',
      narration:
        'Y la cuarta: Productividad y Rendimiento. Pesos, ganancia diaria, eficiencia económica: qué lotes engordan y qué lotes te cuestan plata. Acá también está la evolución del peso de tus animales, que es la curva que hay que cuidar. Entre esta y la de preñez, tenés el pulso del negocio.',
    },
    {
      id: 'exportar-cierre',
      route: REPORTES,
      text: 'Cada card te da el número sin entrar — el termómetro del campo a un golpe de vista. Tus datos, tus decisiones.',
      narration:
        '¿Y para qué sirve todo esto? Para decidir con datos, no con corazonadas: cada card de la portada ya te da el número principal sin entrar al reporte, y adentro está el detalle. Y con esto cerramos el círculo completo: cargaste el campo, los animales, el mapa, los papeles y los números. Ahora la app trabaja para vos. Usala, criollo.',
    },
  ],
  completionCheck: async () => false, // se completa haciendo el recorrido, no por datos
};
